/**
 * Walkthrough related commands
 */
import {isUserLoggedIn} from '@Libraries/user.library';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

interface WalkthroughCommands {
  walkthrough: {
    GET_NON_LOGGED_IN_USER_SEEN_WALKTHROUGH: string;
    GET_USER_SEEN_WALKTHROUGH: string;
    GET_USER_LOGGED_IN_SEEN_WALKTHROUGH: string;
    OPEN_USER_INTRODUCTION_MAIN_WALKTHROUGH_POPUP: string;
    OPEN_USER_MAIN_WALKTHROUGH_POPUP: string;
    OPEN_ADD_SECTION_WALKTHROUGH_POPUP: string;
    OPEN_ADD_TABLE_WALKTHROUGH_POPUP: string;
    OPEN_ADD_BUTTON_WALKTHROUGH_POPUP: string;
    OPEN_ADD_SOCIAL_LINKS_WALKTHROUGH_POPUP: string;
    OPEN_ADD_DESIGN_WALKTHROUGH_POPUP: string;
    OPEN_ADD_LINK_WALKTHROUGH_POPUP: string;
    OPEN_ADD_STICKER_WALKTHROUGH_POPUP: string;
    OPEN_ADD_VIDEO_WALKTHROUGH_POPUP: string;
  };
  DISABLE_EDITOR_CONTROLS: string;
  ENABLE_EDITOR_CONTROLS: string;
}

interface WalkthroughLoadOptions {
  isAutosaveEnabled?: boolean;
  isHierarchyLevelOne?: boolean;
  isHierarchyLevelTwo?: boolean;
  isLink?: boolean;
  walkthroughType?: string;
  [key: string]: unknown;
}

interface UserHasSeenResponse {
  userMainWalkthrough: boolean;
  userWalkthroughHierarchyLevelOne: boolean;
  userWalkthroughHierarchyLevelTwo: boolean;
  userWalkthroughSection: boolean;
  userWalkthroughTable: boolean;
  userWalkthroughButton: boolean;
  userWalkthroughSocial: boolean;
  userWalkthroughLink: boolean;
  userWalkthroughDesign: boolean;
}

let CMDS: WalkthroughCommands;

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const cmds = editor.Commands;

  CMDS = window.PMW.emails.commands as unknown as WalkthroughCommands;

  cmds.add(CMDS.walkthrough.GET_NON_LOGGED_IN_USER_SEEN_WALKTHROUGH, async (): Promise<void> => {
    window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name) === '1';
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.name) === '1';
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.name) === '1';
    window.PMW.emails.walkthrough.ADD_SECTION.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.ADD_SECTION.name) === '1';
    window.PMW.emails.walkthrough.ADD_TABLE.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.ADD_TABLE.name) === '1';
    window.PMW.emails.walkthrough.ADD_BUTTON.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.ADD_BUTTON.name) === '1';
    window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.name) === '1';
    window.PMW.emails.walkthrough.ADD_LINK.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.ADD_LINK.name) === '1';
    window.PMW.emails.walkthrough.ADD_DESIGN.userHasSeen = window.PMW.getCacheItem(window.PMW.emails.walkthrough.ADD_DESIGN.name) === '1';
  });

  cmds.add(CMDS.walkthrough.GET_USER_SEEN_WALKTHROUGH, async () => {
    if (isUserLoggedIn()) {
      await editor.runCommand(window.PMW.emails.commands.walkthrough.GET_USER_LOGGED_IN_SEEN_WALKTHROUGH);
    } else {
      await editor.runCommand(window.PMW.emails.commands.walkthrough.GET_NON_LOGGED_IN_USER_SEEN_WALKTHROUGH);
    }
  });

  cmds.add(CMDS.walkthrough.GET_USER_LOGGED_IN_SEEN_WALKTHROUGH, async () => {
    try {
      const userHasSeen = await window.PMW.readLocal('emailmaker/getUserHasSeenWalkthrough') as UserHasSeenResponse;
      window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.userHasSeen = userHasSeen.userMainWalkthrough;
      window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen = userHasSeen.userWalkthroughHierarchyLevelOne;
      window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen = userHasSeen.userWalkthroughHierarchyLevelTwo;
      window.PMW.emails.walkthrough.ADD_SECTION.userHasSeen = userHasSeen.userWalkthroughSection;
      window.PMW.emails.walkthrough.ADD_TABLE.userHasSeen = userHasSeen.userWalkthroughTable;
      window.PMW.emails.walkthrough.ADD_BUTTON.userHasSeen = userHasSeen.userWalkthroughButton;
      window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.userHasSeen = userHasSeen.userWalkthroughSocial;
      window.PMW.emails.walkthrough.ADD_LINK.userHasSeen = userHasSeen.userWalkthroughLink;
      window.PMW.emails.walkthrough.ADD_DESIGN.userHasSeen = userHasSeen.userWalkthroughDesign;
    } catch (e) {
      console.error('User has seen walkthrough data does not exist for this user id');
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_USER_INTRODUCTION_MAIN_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughIntroductionContainer(window.PMW.emails.walkthrough.INTRODUCTION_MAIN_WALKTHROUGH.name);
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.INTRODUCTION_MAIN_WALKTHROUGH.name),
        window.PMW.util.site_url('emailmaker/userMainWalkthroughIntroductionPopup/' + window.PMW.emails.walkthrough.INTRODUCTION_MAIN_WALKTHROUGH.name),
        {},
        initialiseStartWalkThroughListeners
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_USER_MAIN_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name),
        window.PMW.util.site_url('emailmaker/userMainWalkthroughPopup/' + window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name),
        {
          isAutosaveEnabled: editor.StorageManager.isAutosave(),
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_ADD_SECTION_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.ADD_SECTION.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.ADD_SECTION.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.ADD_SECTION.name),
        window.PMW.util.site_url('emailmaker/openAddSectionWalkThroughPopup'),
        {
          isHierarchyLevelOne: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
          isHierarchyLevelTwo: false,
          isLink: false,
          walkthroughType: window.PMW.emails.walkthrough.ADD_SECTION.name,
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.ADD_SECTION.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.ADD_SECTION.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_ADD_TABLE_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.ADD_TABLE.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.ADD_TABLE.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.ADD_TABLE.name),
        window.PMW.util.site_url('emailmaker/openAddTableWalkThroughPopup'),
        {
          isHierarchyLevelOne: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
          isHierarchyLevelTwo: false,
          isLink: false,
          walkthroughType: window.PMW.emails.walkthrough.ADD_TABLE.name,
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.ADD_TABLE.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.ADD_TABLE.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_ADD_BUTTON_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.ADD_BUTTON.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.ADD_BUTTON.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.ADD_BUTTON.name),
        window.PMW.util.site_url('emailmaker/openAddButtonsWalkThroughPopup'),
        {
          isHierarchyLevelOne: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
          isHierarchyLevelTwo: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
          isLink: !window.PMW.emails.walkthrough.ADD_LINK.userHasSeen,
          walkthroughType: window.PMW.emails.walkthrough.ADD_BUTTON.name,
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.ADD_BUTTON.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.ADD_BUTTON.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_ADD_SOCIAL_LINKS_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.name),
        window.PMW.util.site_url('emailmaker/openAddSocialLinksWalkThroughPopup'),
        {
          isHierarchyLevelOne: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
          isHierarchyLevelTwo: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
          isLink: false,
          walkthroughType: window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.name,
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_ADD_DESIGN_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.ADD_DESIGN.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.ADD_DESIGN.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.ADD_DESIGN.name),
        window.PMW.util.site_url('emailmaker/openAddDesignWalkThroughPopup'),
        {
          isHierarchyLevelOne: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
          isHierarchyLevelTwo: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
          isLink: !window.PMW.emails.walkthrough.ADD_LINK.userHasSeen,
          walkthroughType: window.PMW.emails.walkthrough.ADD_DESIGN.name,
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.ADD_DESIGN.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.ADD_DESIGN.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_ADD_LINK_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.ADD_LINK.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.ADD_LINK.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.ADD_LINK.name),
        window.PMW.util.site_url('emailmaker/openAddLinkWalkThroughPopup'),
        {
          isHierarchyLevelOne: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
          isHierarchyLevelTwo: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
          isLink: !window.PMW.emails.walkthrough.ADD_LINK.userHasSeen,
          walkthroughType: window.PMW.emails.walkthrough.ADD_LINK.name,
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.ADD_LINK.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.ADD_LINK.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_ADD_STICKER_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.ADD_LINK.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.ADD_LINK.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.ADD_LINK.name),
        window.PMW.util.site_url('emailmaker/openAddStickerWalkThroughPopup'),
        {
          isHierarchyLevelOne: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
          isHierarchyLevelTwo: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
          isLink: !window.PMW.emails.walkthrough.ADD_LINK.userHasSeen,
          walkthroughType: window.PMW.emails.walkthrough.ADD_LINK.name,
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.ADD_LINK.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.ADD_LINK.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.walkthrough.OPEN_ADD_VIDEO_WALKTHROUGH_POPUP, () => {
    if (!window.PMW.emails.walkthrough.ADD_LINK.userHasSeen && !isWalkThroughPopupOpen()) {
      initialiseWalkthroughContainer(window.PMW.emails.walkthrough.ADD_LINK.name);
      window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = true;
      window.PMW.load(
        getWalkthroughHtmlContainer(window.PMW.emails.walkthrough.ADD_LINK.name),
        window.PMW.util.site_url('emailmaker/openAddVideoWalkThroughPopup'),
        {
          isHierarchyLevelOne: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
          isHierarchyLevelTwo: !window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
          isLink: !window.PMW.emails.walkthrough.ADD_LINK.userHasSeen,
          walkthroughType: window.PMW.emails.walkthrough.ADD_LINK.name,
        },
        (_id: string, options: WalkthroughLoadOptions) => {
          initialiseWalkThroughListeners(window.PMW.emails.walkthrough.ADD_LINK.name);
          initialiseFirstPopupDimensionsAndPosition(window.PMW.emails.walkthrough.ADD_LINK.name);
          editor.runCommand(window.PMW.emails.commands.DISABLE_EDITOR_CONTROLS, options);
        }
      );
    }
  });

  cmds.add(CMDS.DISABLE_EDITOR_CONTROLS, () => {
    const blocksManager = document.querySelector('.js-blocks-manager') as HTMLElement;
    const canvasBox = document.querySelector('.html-editor-canvas-box') as HTMLElement;

    if (blocksManager) {
      blocksManager.style.pointerEvents = 'none';
    }
    if (canvasBox) {
      canvasBox.style.pointerEvents = 'none';
    }
  });

  cmds.add(CMDS.ENABLE_EDITOR_CONTROLS, () => {
    const blocksManager = document.querySelector('.js-blocks-manager') as HTMLElement;
    const canvasBox = document.querySelector('.html-editor-canvas-box') as HTMLElement;
    
    if (blocksManager) {
      blocksManager.style.pointerEvents = 'unset';
    }
    if (canvasBox) {
      canvasBox.style.pointerEvents = 'unset';
    }
  });

  function getWalkthroughHtmlContainer(walkthroughType: string): JQuery {
    return $(window.PMW.emails.WALKTHROUGH_CONTAINER_CLASSNAME + '.' + walkthroughType);
  }

  function isWalkThroughPopupOpen(): boolean {
    return $('.user-walkthrough-popup:visible').length > 0 || window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN;
  }

  function initialiseFirstPopupDimensionsAndPosition(walkthroughType: string): void {
    const mainContainer = $('.user-walkthrough-popup' + '.' + walkthroughType + ':visible');
    const currentWalkThroughElement = mainContainer.find('.walkthrough-container').not('._hidden');
    
    try {
      handleFirstStepWalkthrough(true);
      changePositionAndDimensionsOfPopup(mainContainer, currentWalkThroughElement);
    } catch (e) {
      console.error(e);
      disableUserWalkThrough(walkthroughType);
    }
  }

  function initialiseWalkthroughIntroductionContainer(containerClassName: string): void {
    const mainContainer = document.querySelector('body');
    const walkthroughContainer = document.createElement('div');
    const hiddenForPhone = containerClassName !== window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name && 
      containerClassName !== window.PMW.emails.walkthrough.INTRODUCTION_MAIN_WALKTHROUGH.name ? 
      'hidden-tablet hidden-phone' : '';

    if (mainContainer) {
      mainContainer.classList.add('walkthrough');
    }
    walkthroughContainer.id = 'email-editor-walkthrough-container ' + containerClassName;
    walkthroughContainer.className = 'js-walkthrough-container ' + containerClassName + '  ' + hiddenForPhone;
    
    if (mainContainer) {
      mainContainer.appendChild(walkthroughContainer);
    }
  }

  function initialiseWalkthroughContainer(containerClassName: string): void {
    const mainContainer = document.querySelector('.html-editor-user-options-container');
    const walkthroughContainer = document.createElement('div');
    const hiddenForPhone = containerClassName !== window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name && 
      containerClassName !== window.PMW.emails.walkthrough.INTRODUCTION_MAIN_WALKTHROUGH.name ? 
      'hidden-tablet hidden-phone' : '';

    walkthroughContainer.id = 'email-editor-walkthrough-container ' + containerClassName;
    walkthroughContainer.className = 'js-walkthrough-container ' + containerClassName + '  ' + hiddenForPhone;
    
    if (mainContainer) {
      mainContainer.appendChild(walkthroughContainer);
    }
  }

  function initialiseWalkThroughListeners(userWalkThroughType: string): void {
    window.PMW.onClick($('.user-walkthrough-popup' + '.' + userWalkThroughType + ' .js-next-button' + ':visible'), null, function () {
      const elem = $('.user-walkthrough-popup' + '.' + userWalkThroughType + ':visible').find('.walkthrough-container').not('._hidden');
      const nextElem = elem.next();
      elem.addClass('_hidden');
      nextElem.removeClass('_hidden');
      moveWalkthroughToNewPopup(nextElem, userWalkThroughType);
    });

    window.PMW.onClick($('.user-walkthrough-popup' + '.' + userWalkThroughType + ' .js-prev-button' + ':visible'), null, function () {
      const elem = $('.user-walkthrough-popup' + '.' + userWalkThroughType + ':visible').find('.walkthrough-container').not('._hidden');
      const prevElem = elem.prev();
      elem.addClass('_hidden');
      prevElem.removeClass('_hidden');
      moveWalkthroughToNewPopup(prevElem, userWalkThroughType);
    });

    window.PMW.onClick($('.js-success-button-popup'), null, function () {
      disableUserWalkThrough(userWalkThroughType);
    });

    window.PMW.onClick($('.js-skip-walkthrough'), null, function () {
      disableUserWalkThrough(userWalkThroughType);
    });
  }

  function initialiseStartWalkThroughListeners(): void {
    window.PMW.onClick($('.js-start-walkthrough'), null, function () {
      $('#introduction-main-user-walkthrough').addClass('_hidden');
      $('body').removeClass('walkthrough');
      editor.runCommand(window.PMW.emails.commands.walkthrough.OPEN_USER_MAIN_WALKTHROUGH_POPUP);
    });
    
    window.PMW.onClick($('.js-skip-walkthrough-introduction'), null, function () {
      $('#introduction-main-user-walkthrough').addClass('_hidden');
      $('body').removeClass('walkthrough');
      disableUserWalkThrough(window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name);
    });
  }

  function disableUserWalkThrough(userWalkThroughType: string): void {
    $('.' + userWalkThroughType).addClass('_hidden');
    window.PMW.emails.IS_WALKTHROUGH_POPUP_OPEN = false;
    editor.runCommand(window.PMW.emails.commands.ENABLE_EDITOR_CONTROLS);
    
    switch (userWalkThroughType) {
      case window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name:
        disableMainWalkthroughPopup();
        break;
      case window.PMW.emails.walkthrough.ADD_SECTION.name:
        disableAddSectionPopup();
        break;
      case window.PMW.emails.walkthrough.ADD_TABLE.name:
        disableAddTablePopup();
        break;
      case window.PMW.emails.walkthrough.ADD_BUTTON.name:
        disableAddButtonPopup();
        break;
      case window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.name:
        disableAddSocialLinksPopup();
        break;
      case window.PMW.emails.walkthrough.ADD_LINK.name:
        disableAddLinkPopup();
        break;
      case window.PMW.emails.walkthrough.ADD_DESIGN.name:
        disableAddDesignPopup();
        break;
    }
  }

  function disableMainWalkthroughPopup(): void {
    window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.userHasSeen = true;
    if (!isUserLoggedIn()) {
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.MAIN_WALKTHROUGH.name, '1');
    } else {
      void window.PMW.write(window.PMW.util.site_url('emailmarketing/markEmailMainWalkthroughSeen'));
    }
  }

  function disableAddSectionPopup(): void {
    window.PMW.emails.walkthrough.ADD_SECTION.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen = true;

    if (!isUserLoggedIn()) {
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.ADD_SECTION.name, '1');
    } else {
      void window.PMW.write(window.PMW.util.site_url('emailmarketing/markSectionWalkthroughSeen'), {
        isHierarchyLevelOne: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
      });
    }
  }

  function disableAddTablePopup(): void {
    window.PMW.emails.walkthrough.ADD_TABLE.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen = true;

    if (!isUserLoggedIn()) {
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.ADD_TABLE.name, '1');
    } else {
      void window.PMW.write(window.PMW.util.site_url('emailmarketing/markTableWalkthroughSeen'), {
        isHierarchyLevelOne: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
      });
    }
  }

  function disableAddButtonPopup(): void {
    window.PMW.emails.walkthrough.ADD_BUTTON.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen = true;
    window.PMW.emails.walkthrough.ADD_LINK.userHasSeen = true;

    if (!isUserLoggedIn()) {
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.ADD_LINK.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.ADD_BUTTON.name, '1');
    } else {
      void window.PMW.write(window.PMW.util.site_url('emailmarketing/markButtonWalkthroughSeen'), {
        isHierarchyLevelOne: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
        isHierarchyLevelTwo: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
        isLink: window.PMW.emails.walkthrough.ADD_LINK.userHasSeen,
      });
    }
  }

  function disableAddSocialLinksPopup(): void {
    window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen = true;

    if (!isUserLoggedIn()) {
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.ADD_SOCIAL_LINKS.name, '1');
    } else {
      void window.PMW.write(window.PMW.util.site_url('emailmarketing/markSocialWalkthroughSeen'), {
        isHierarchyLevelOne: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
        isHierarchyLevelTwo: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
      });
    }
  }

  function disableAddLinkPopup(): void {
    window.PMW.emails.walkthrough.ADD_LINK.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen = true;

    if (!isUserLoggedIn()) {
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.ADD_LINK.name, '1');
    } else {
      void window.PMW.write(window.PMW.util.site_url('emailmarketing/markLinkWalkthroughSeen'), {
        isHierarchyLevelOne: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
        isHierarchyLevelTwo: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
      });
    }
  }

  function disableAddDesignPopup(): void {
    window.PMW.emails.walkthrough.ADD_DESIGN.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen = true;
    window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen = true;
    window.PMW.emails.walkthrough.ADD_LINK.userHasSeen = true;

    if (!isUserLoggedIn()) {
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.ADD_DESIGN.name, '1');
      window.PMW.setCacheItem(window.PMW.emails.walkthrough.ADD_LINK.name, '1');
    } else {
      void window.PMW.write(window.PMW.util.site_url('emailmarketing/markDesignWalkthroughSeen'), {
        isHierarchyLevelOne: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_ONE.userHasSeen,
        isHierarchyLevelTwo: window.PMW.emails.walkthrough.HIERARCHY_LEVEL_TWO.userHasSeen,
        isLink: window.PMW.emails.walkthrough.ADD_LINK.userHasSeen,
      });
    }
  }

  function moveWalkthroughToNewPopup(newElement: JQuery, userWalkThroughType: string): void {
    const modalContainer = $('.user-walkthrough-popup' + '.' + userWalkThroughType + ':visible');
    const stepNumber = newElement[0].getAttribute('data-step-number');
    const headerTitle = newElement[0].getAttribute('data-header-title');
    const totalSteps = modalContainer[0].getAttribute('data-total-steps');
    const isLastStep = parseInt(stepNumber ?? '0') === parseInt(totalSteps ?? '0');
    const isFirstStep = stepNumber === '1';

    $('.js-step-number-walkthrough').html(`${stepNumber} / ${totalSteps}`);

    if (headerTitle) {
      $('.js-header-title-walkthrough').html(headerTitle);
    }

    try {
      changePositionAndDimensionsOfPopup(modalContainer, newElement);
      handleFirstStepWalkthrough(isFirstStep);
      handleLastStepWalkthrough(isLastStep);
    } catch (e) {
      console.error(e);
      disableUserWalkThrough(userWalkThroughType);
    }
  }
}

export function changePositionAndDimensionsOfPopup(modalContainer: JQuery, newElement: JQuery): void {
  const positionContainerClassname = newElement[0].getAttribute('data-position-container');
  const positionOffsetLeft = newElement[0].getAttribute('data-left-offset');
  const positionOffsetTop = newElement[0].getAttribute('data-top-offset');
  const modalHeight = newElement[0].getAttribute('data-modal-height');
  const modalWidth = newElement[0].getAttribute('data-modal-width');
  const nextElementPosition = $(`.${positionContainerClassname}`).offset();

  if (nextElementPosition) {
    modalContainer.css({
      'top': `${nextElementPosition.top + parseInt(positionOffsetTop ?? '0')}px`,
      'left': `${nextElementPosition.left + parseInt(positionOffsetLeft ?? '0')}px`,
      'width': `${modalWidth}px`,
      'height': `${modalHeight}px`,
    });
  }
}

export function handleLastStepWalkthrough(isLastStep: boolean): void {
  const stepNumber = $('.js-step-number-walkthrough');
  const successButton = $('.js-success-walkthrough');
  const nextButton = $('.js-next-walkthrough');
  const skipLink = $('.js-skip-walkthrough');

  if (isLastStep) {
    successButton.removeClass('_hidden');
    nextButton.addClass('_hidden');
    skipLink.addClass('_hidden');
    stepNumber.removeClass('content-primary');
    stepNumber.addClass('color-success');
  } else if (nextButton.hasClass('_hidden')) {
    successButton.addClass('_hidden');
    nextButton.removeClass('_hidden');
    skipLink.removeClass('_hidden');
    stepNumber.addClass('content-primary');
    stepNumber.removeClass('color-success');
  }
}

export function handleFirstStepWalkthrough(isFirstStep: boolean): void {
  const prevButton = $('.js-prev-walkthrough');
  const stepNumber = $('.js-step-number-walkthrough');

  if (isFirstStep) {
    prevButton.addClass('_hidden');
    stepNumber.addClass('extra-spacing-left');
  } else if (prevButton.hasClass('_hidden')) {
    prevButton.removeClass('_hidden');
    stepNumber.removeClass('extra-spacing-left');
  }
}